import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Step1Goals from './Step1Goals';
import Step3ShoppingCart from './Step3ShoppingCart';
import Step4Review from './Step4Review';
import { useNutritionCart } from '../../hooks/useNutritionCart';
import { updateMacroOverrides, getActiveGoal, deleteNutritionGoal } from '../../services/nutritionService';
import { event as gaEvent } from '../../utils/analytics';
import { getClientNutritionPlanExitPath } from '../../utils/affiliateNavigation';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { lbsToDisplayWeight } from '../../utils/weightUnits';
import './NutritionPlannerWizard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const STEPS = [
  { id: 1, title: 'Goals', icon: 'fa-bullseye', description: 'Set your nutrition targets' },
  { id: 2, title: 'Build Cart', icon: 'fa-shopping-basket', description: 'Select foods for your plan' },
  { id: 3, title: 'Review & Shop', icon: 'fa-shopping-cart', description: 'Review and shop with Instacart' }
];

const DRAFT_KEY = 'nutritionPlannerDraft';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const DEFAULT_GOALS = {
  currentWeight: '',
  goalWeight: '',
  mealsPerDay: 4,
  activityLevel: 'moderate',
  goalType: 'maintain',
  gender: 'male',
  age: 30,
  height: 70
};

const readDraft = (hasGoalId) => {
  if (hasGoalId) return null;
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft?.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) return null;
    return draft;
  } catch {
    return null;
  }
};

const NutritionPlannerWizard = ({
  embedded = false,
  propGoalId = null,
  propClientId = null,
  propOrgMemberId = null,
  onClose = null,
} = {}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const weightUnit = useWeightUnit();

  // Props override URL params
  const goalId = propGoalId || searchParams.get('goalId');
  const clientId = propClientId || searchParams.get('clientId');
  const orgMemberId = propOrgMemberId || searchParams.get('orgMemberId');

  // Active goal for summary view (shown when user lands without goalId)
  const [activeGoal, setActiveGoal] = useState(null);
  const [activeGoalLoading, setActiveGoalLoading] = useState(!goalId);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Restore draft once on mount (only when there's no server-side goalId)
  const draft = readDraft(!!goalId);

  const [currentStep, setCurrentStep] = useState(() => draft?.currentStep ?? 1);
  const [loadingPlan, setLoadingPlan] = useState(!!goalId || !!orgMemberId);
  const [currentGoalId, setCurrentGoalId] = useState(goalId);
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  const [macroOverrides, setMacroOverrides] = useState(() => draft?.macroOverrides ?? null);
  const [wizardData, setWizardData] = useState(() => draft?.wizardData ?? {
    goals: DEFAULT_GOALS,
    results: null
  });
  
  const cart = useNutritionCart();
  
  // Ref to track if the "loaded" toast has been shown (prevents duplicate toasts)
  const loadedToastShownRef = useRef(false);
  // Prevents double auto-save / duplicate toasts (e.g. React Strict Mode re-running effects)
  const autoSaveNewGoalRef = useRef(false);
  
  const isCoachMode = !!clientId;
  const isBusinessMode = !!orgMemberId;

  const handleExit = useCallback(() => {
    if (embedded && onClose) {
      onClose();
    } else if (isCoachMode) {
      navigate(getClientNutritionPlanExitPath());
    } else if (isBusinessMode) {
      navigate('/business/dashboard');
    } else {
      navigate('/nutrition/planner');
    }
  }, [embedded, onClose, isCoachMode, isBusinessMode, navigate]);

  // When orgMemberId is set without a goalId, look up the client's active plan
  useEffect(() => {
    if (!orgMemberId || goalId) {
      if (!goalId && !orgMemberId) setLoadingPlan(false);
      return;
    }
    const fetchOrgClientPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setLoadingPlan(false); return; }
        const res = await fetch(`${API_URL}/business-fitness/clients/${orgMemberId}/nutrition-plans`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const plans = data.plans || data || [];
          const active = plans.find(p => p.isActive);
          if (active) {
            setActiveGoal(active);
            setActiveGoalLoading(false);
            setLoadingPlan(false);
            return;
          }
        }
      } catch (err) {
        console.error('Failed to fetch org client nutrition plans:', err);
      }
      setActiveGoalLoading(false);
      setLoadingPlan(false);
    };
    fetchOrgClientPlan();
  }, [orgMemberId, goalId]);

  // Fetch active goal when landing without goalId (to show summary) — personal mode only
  useEffect(() => {
    if (goalId || orgMemberId) {
      setActiveGoalLoading(false);
      return;
    }
    const fetchActive = async () => {
      try {
        const goal = await getActiveGoal();
        setActiveGoal(goal);
      } catch {
        // No active goal — that's fine, show wizard
      } finally {
        setActiveGoalLoading(false);
      }
    };
    fetchActive();
  }, [goalId, orgMemberId]);

  const handleDeletePlan = async () => {
    if (!activeGoal?._id) return;
    setDeleting(true);
    try {
      await deleteNutritionGoal(activeGoal._id);
      toast.success('Nutrition plan deleted');
      setActiveGoal(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };
  
  // Fetch affiliate info if user is creating a plan for a client
  useEffect(() => {
    const fetchAffiliateInfo = async () => {
      if (!clientId) {
        // Not in coach mode, no need to fetch affiliate info
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/affiliate/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Affiliate metrics response:', data); // Debug logging
          setAffiliateInfo({
            affiliateId: data.affiliateId, // MongoDB ObjectId from metrics endpoint response
            businessName: data.businessName
          });
        }
      } catch (err) {
        console.error('Failed to fetch affiliate info:', err);
      }
    };
    
    fetchAffiliateInfo();
  }, [clientId]);

  // Load existing plan if goalId is provided
  useEffect(() => {
    const loadExistingPlan = async () => {
      if (!goalId) {
        setLoadingPlan(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoadingPlan(false);
          return;
        }
        
        // Fetch the goal data
        const goalResponse = await fetch(`${API_URL}/nutrition/goals/${goalId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!goalResponse.ok) {
          toast.error('Could not load the saved plan');
          setLoadingPlan(false);
          return;
        }
        
        const goalData = await goalResponse.json();
        
        console.log('Loaded goal data:', goalData);
        
        // Build the results object from goal data
        const hasTargets = goalData.dailyCalories && goalData.dailyCalories > 0;
        const loadedResults = hasTargets ? {
          dailyTargets: {
            dailyCalories: goalData.dailyCalories,
            dailyProtein: goalData.dailyProtein,
            dailyCarbs: goalData.dailyCarbs,
            dailyFat: goalData.dailyFat
          },
          perMealTargets: {
            perMealCalories: goalData.perMealCalories,
            perMealProtein: goalData.perMealProtein,
            perMealCarbs: goalData.perMealCarbs,
            perMealFat: goalData.perMealFat
          }
        } : null;
        
        console.log('Setting results:', loadedResults);
        
        // Load macro overrides if they exist
        if (goalData.macroOverrides) {
          const hasOverrides = Object.values(goalData.macroOverrides).some(v => v !== null && v !== undefined);
          if (hasOverrides) {
            setMacroOverrides(goalData.macroOverrides);
          }
        }
        
        // Populate goals and results
        setWizardData({
          goals: {
            currentWeight: goalData.currentWeight || '',
            goalWeight: goalData.goalWeight || '',
            mealsPerDay: goalData.mealsPerDay || 4,
            activityLevel: goalData.activityLevel || 'moderate',
            goalType: goalData.goalType || 'maintain',
            gender: goalData.gender || 'male',
            age: goalData.age || 30,
            height: goalData.height || 70
          },
          results: loadedResults
        });
        
        // Set the goal ID first to prevent saveGoal from running
        setCurrentGoalId(goalId);
        
        // Load week plan if it exists
        if (goalData.weekPlan?.days?.length > 0) {
          console.log('Loading week plan:', goalData.weekPlan.days);
          cart.loadWeekPlan(goalData.weekPlan.days);
          // If there's a week plan, start from Build Cart step (step 2)
          setCurrentStep(2);
          // Only show toast once (prevents duplicate due to React StrictMode or re-renders)
          if (!loadedToastShownRef.current) {
            loadedToastShownRef.current = true;
            toast.success('Loaded your saved plan!');
          }
        } else if (hasTargets) {
          // If we have calculated targets, start from Build Cart
          setCurrentStep(2);
          // Skip toast here - the plan is loaded, user can continue
        } else {
          // Otherwise start from step 1
          setCurrentStep(1);
          // Skip toast here - just start fresh
        }
      } catch (error) {
        console.error('Error loading plan:', error);
        toast.error('Failed to load plan');
      } finally {
        setLoadingPlan(false);
      }
    };
    
    loadExistingPlan();
    // Note: cart.loadWeekPlan is a stable reference from useCallback, so this won't cause infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  // Transform week plan for database storage
  const transformWeekPlanForSave = useCallback((weekPlan) => {
    return weekPlan.map(day => ({
      dayNumber: day.dayNumber,
      date: day.date,
      dayName: day.dayName,
      meals: {
        breakfast: transformMealForSave(day.meals.breakfast),
        lunch: transformMealForSave(day.meals.lunch),
        dinner: transformMealForSave(day.meals.dinner),
        snacks: transformMealForSave(day.meals.snacks)
      }
    }));
  }, []);
  
  // Transform a single meal for saving
  const transformMealForSave = (meal) => {
    return {
      foods: (meal.foods || []).map(item => ({
        fdcId: item.food?.fdcId,
        description: item.food?.description,
        category: item.food?.category,
        brandOwner: item.food?.brandOwner,
        nutrients: item.food?.nutrients,
        quantity: item.quantity,
        servingSize: item.servingSize
      })),
      menuItems: (meal.menuItems || []).map(item => ({
        menuItemId: item.menuItem?._id,
        title: item.menuItem?.title,
        image: item.menuItem?.image,
        nutrition: item.menuItem?.nutrition,
        servings: item.servings
      }))
    };
  };

  // Persist draft to localStorage whenever key state changes (only before a goalId is established)
  useEffect(() => {
    if (currentGoalId || loadingPlan) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        wizardData,
        currentStep,
        macroOverrides,
        savedAt: Date.now()
      }));
    } catch {
      // localStorage unavailable — silently skip
    }
  }, [wizardData, currentStep, macroOverrides, currentGoalId, loadingPlan]);

  // Auto-save week plan when it changes (debounced)
  useEffect(() => {
    // Guard: Don't auto-save if no goalId or still loading
    if (!currentGoalId || loadingPlan) return;
    
    // Capture current goalId value for the timeout callback
    const goalIdToSave = currentGoalId;
    
    const saveTimeout = setTimeout(async () => {
      // Double-check goalId is still valid before saving
      if (!goalIdToSave || goalIdToSave === 'undefined') {
        console.warn('[Auto-save] Skipping save - invalid goalId:', goalIdToSave);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const transformedDays = transformWeekPlanForSave(cart.weekPlan);
        
        await fetch(`${API_URL}/nutrition/goals/${goalIdToSave}/weekplan`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            weekPlan: {
              startDate: cart.startDate,
              days: transformedDays
            }
          })
        });
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 2000); // Save 2 seconds after last change
    
    return () => clearTimeout(saveTimeout);
  }, [cart.weekPlan, cart.startDate, currentGoalId, loadingPlan, transformWeekPlanForSave]);

  // Auto-save macro overrides when they change (debounced)
  useEffect(() => {
    if (!currentGoalId || loadingPlan) return;
    
    const goalIdToSave = currentGoalId;
    
    const saveTimeout = setTimeout(async () => {
      if (!goalIdToSave || goalIdToSave === 'undefined') return;
      
      try {
        await updateMacroOverrides(goalIdToSave, macroOverrides || {
          dailyCalories: null,
          dailyProtein: null,
          dailyCarbs: null,
          dailyFat: null
        });
      } catch (error) {
        console.error('Failed to save macro overrides:', error);
      }
    }, 1500);
    
    return () => clearTimeout(saveTimeout);
  }, [macroOverrides, currentGoalId, loadingPlan]);

  // Helper to get effective daily targets (overrides take precedence)
  const getEffectiveDailyTargets = useCallback(() => {
    if (!wizardData.results?.dailyTargets) return null;
    const dt = wizardData.results.dailyTargets;
    const mealsPerDay = wizardData.goals.mealsPerDay || 4;
    
    const effectiveCalories = macroOverrides?.dailyCalories ?? dt.dailyCalories;
    const effectiveProtein = macroOverrides?.dailyProtein ?? dt.dailyProtein;
    const effectiveCarbs = macroOverrides?.dailyCarbs ?? dt.dailyCarbs;
    const effectiveFat = macroOverrides?.dailyFat ?? dt.dailyFat;
    
    return {
      dailyCalories: effectiveCalories,
      dailyProtein: effectiveProtein,
      dailyCarbs: effectiveCarbs,
      dailyFat: effectiveFat,
      perMealCalories: Math.round(effectiveCalories / mealsPerDay),
      perMealProtein: Math.round(effectiveProtein / mealsPerDay),
      perMealCarbs: Math.round(effectiveCarbs / mealsPerDay),
      perMealFat: Math.round(effectiveFat / mealsPerDay)
    };
  }, [wizardData.results, wizardData.goals.mealsPerDay, macroOverrides]);

  // Update wizard data
  const updateWizardData = useCallback((section, data) => {
    setWizardData(prev => ({
      ...prev,
      [section]: typeof data === 'function' ? data(prev[section]) : data
    }));
  }, []);

  // Create or update nutrition goal when results are calculated
  const saveGoal = useCallback(async () => {
    if (!wizardData.results) return null;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to save your plan', { id: 'login-required' });
      return null;
    }
    
    try {
      const goalData = {
        ...wizardData.goals,
        ...wizardData.results.dailyTargets,
        ...wizardData.results.perMealTargets,
        goalType: wizardData.goals.goalType,
        mealsPerDay: wizardData.goals.mealsPerDay,
        activityLevel: wizardData.goals.activityLevel,
        name: `${wizardData.goals.goalType.charAt(0).toUpperCase() + wizardData.goals.goalType.slice(1)} Plan`,
        isActive: true,
        macroOverrides: macroOverrides || undefined
      };
      
      // If creating a plan for a client (coach functionality)
      if (clientId && !currentGoalId) {
        goalData.clientId = clientId;
      }

      // Business mode: use business-fitness endpoint
      if (orgMemberId && !currentGoalId) {
        const response = await fetch(`${API_URL}/business-fitness/clients/${orgMemberId}/nutrition-plan`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(goalData)
        });
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to save goal');
        }
        const savedData = await response.json();
        const savedGoal = savedData.goal || savedData;
        const newGoalId = savedGoal._id || savedGoal.id;
        if (newGoalId) {
          setCurrentGoalId(newGoalId);
          localStorage.removeItem(DRAFT_KEY);
          toast.success('Client plan created! Progress will auto-save.', { id: 'nutrition-plan-created' });
          gaEvent({ action: 'create_client_plan', category: 'Business', label: orgMemberId });
        }
        return newGoalId;
      }
      
      const method = currentGoalId ? 'PUT' : 'POST';
      const url = currentGoalId 
        ? `${API_URL}/nutrition/goals/${currentGoalId}`
        : `${API_URL}/nutrition/goals`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(goalData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save goal');
      }
      
      const savedData = await response.json();
      const savedGoal = savedData.goal || savedData;
      const newGoalId = savedGoal._id || savedGoal.id || currentGoalId;
      
      if (!currentGoalId && newGoalId) {
        setCurrentGoalId(newGoalId);
        localStorage.removeItem(DRAFT_KEY);
        const successMsg = clientId
          ? 'Client plan created! Progress will auto-save.'
          : 'Plan created! Your progress will auto-save.';
        toast.success(successMsg, { id: 'nutrition-plan-created' });
        if (clientId) {
          gaEvent({ action: 'create_client_plan', category: 'Coach', label: clientId });
        }
      }
      
      return newGoalId;
    } catch (error) {
      console.error('Failed to save goal:', error);
      return null;
    }
  }, [wizardData.results, wizardData.goals, currentGoalId, clientId, orgMemberId, macroOverrides]);

  // Save goal when moving from Step 1 (Goals) to Step 2 (Build Cart)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (currentStep !== 2 || !wizardData.results || currentGoalId || loadingPlan || !token) {
      return;
    }
    if (autoSaveNewGoalRef.current) return;
    autoSaveNewGoalRef.current = true;
    void saveGoal().then((id) => {
      if (!id) autoSaveNewGoalRef.current = false;
    });
  }, [currentStep, wizardData.results, currentGoalId, loadingPlan, saveGoal]);

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return wizardData.results !== null;
      case 2:
        return cart.weekItemCount > 0; // Need at least one item in the week plan
      case 3:
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (canGoNext() && currentStep < STEPS.length) {
      if (currentStep === 1 && !localStorage.getItem('token')) {
        toast.error('Please log in to save your plan', { id: 'login-required' });
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    // Only allow going back or to completed steps
    if (step < currentStep) {
      setCurrentStep(step);
    } else if (step === currentStep + 1 && canGoNext()) {
      setCurrentStep(step);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {STEPS.map((step, index) => {
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        const isAccessible = step.id <= currentStep || (step.id === currentStep + 1 && canGoNext());
        
        return (
          <React.Fragment key={step.id}>
            <div 
              className={`wizard-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isAccessible ? 'accessible' : ''}`}
              onClick={() => isAccessible && goToStep(step.id)}
            >
              <div className="step-circle">
                {isCompleted ? (
                  <i className="fas fa-check"></i>
                ) : (
                  <i className={`fas ${step.icon}`}></i>
                )}
              </div>
              <div className="step-info">
                <span className="step-title">{step.title}</span>
                <span className="step-description">{step.description}</span>
              </div>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`step-connector ${isCompleted ? 'completed' : ''}`}></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    const effectiveTargets = getEffectiveDailyTargets();
    
    switch (currentStep) {
      case 1:
        return (
          <Step1Goals
            goals={wizardData.goals}
            results={wizardData.results}
            onGoalsChange={(goals) => updateWizardData('goals', goals)}
            onResultsChange={(results) => updateWizardData('results', results)}
            macroOverrides={macroOverrides}
            onMacroOverridesChange={setMacroOverrides}
          />
        );
      case 2:
        return (
          <Step3ShoppingCart
            targets={effectiveTargets ? {
              perMealCalories: effectiveTargets.perMealCalories,
              perMealProtein: effectiveTargets.perMealProtein,
              perMealCarbs: effectiveTargets.perMealCarbs,
              perMealFat: effectiveTargets.perMealFat
            } : wizardData.results?.perMealTargets}
            dailyTargets={effectiveTargets || wizardData.results?.dailyTargets}
            cart={cart}
            mealsPerDay={wizardData.goals.mealsPerDay}
            isCoachMode={isCoachMode}
            affiliateId={affiliateInfo?.affiliateId}
          />
        );
      case 3:
        return (
          <Step4Review
            wizardData={wizardData}
            cart={cart}
            currentGoalId={currentGoalId}
          />
        );
      default:
        return null;
    }
  };

  const rootClassName = `nutrition-planner-wizard${embedded ? ' nutrition-planner-wizard--embedded' : ''}`;

  // Show loading state while loading existing plan
  if (loadingPlan || activeGoalLoading) {
    return (
      <div className={rootClassName}>
        {!embedded && (
          <div className="wizard-header">
            <h1>
              <i className="fas fa-utensils"></i> Nutrition Planner
            </h1>
            <p className="wizard-subtitle">Loading your saved plan...</p>
          </div>
        )}
        <div className="wizard-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading your nutrition plan...</p>
        </div>
      </div>
    );
  }

  // Summary view — shown when an active goal exists and user hasn't clicked Manage yet.
  // In embedded mode, skip the summary and jump straight to editing the active goal.
  if (!goalId && activeGoal) {
    if (embedded) {
      // In embedded mode, auto-load the active goal for editing
      if (!currentGoalId || currentGoalId !== activeGoal._id) {
        setCurrentGoalId(activeGoal._id);
        setLoadingPlan(true);
        // Trigger loading the existing plan by setting goalId-equivalent state
        const loadEmbeddedGoal = async () => {
          try {
            const token = localStorage.getItem('token');
            if (!token) { setLoadingPlan(false); return; }
            const goalResponse = await fetch(`${API_URL}/nutrition/goals/${activeGoal._id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!goalResponse.ok) { setLoadingPlan(false); return; }
            const goalData = await goalResponse.json();
            const hasTargets = goalData.dailyCalories && goalData.dailyCalories > 0;
            const loadedResults = hasTargets ? {
              dailyTargets: { dailyCalories: goalData.dailyCalories, dailyProtein: goalData.dailyProtein, dailyCarbs: goalData.dailyCarbs, dailyFat: goalData.dailyFat },
              perMealTargets: { perMealCalories: goalData.perMealCalories, perMealProtein: goalData.perMealProtein, perMealCarbs: goalData.perMealCarbs, perMealFat: goalData.perMealFat }
            } : null;
            if (goalData.macroOverrides) {
              const hasOverrides = Object.values(goalData.macroOverrides).some(v => v !== null && v !== undefined);
              if (hasOverrides) setMacroOverrides(goalData.macroOverrides);
            }
            setWizardData({
              goals: { currentWeight: goalData.currentWeight || '', goalWeight: goalData.goalWeight || '', mealsPerDay: goalData.mealsPerDay || 4, activityLevel: goalData.activityLevel || 'moderate', goalType: goalData.goalType || 'maintain', gender: goalData.gender || 'male', age: goalData.age || 30, height: goalData.height || 70 },
              results: loadedResults
            });
            if (goalData.weekPlan?.days?.length > 0) {
              cart.loadWeekPlan(goalData.weekPlan.days);
              setCurrentStep(2);
            } else if (hasTargets) {
              setCurrentStep(2);
            } else {
              setCurrentStep(1);
            }
          } catch (err) {
            console.error('Error loading embedded plan:', err);
          } finally {
            setLoadingPlan(false);
          }
        };
        loadEmbeddedGoal();
      }
      // Show loading while we transition
      return (
        <div className={rootClassName}>
          <div className="wizard-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading nutrition plan...</p>
          </div>
        </div>
      );
    }

    return (
      <div className={rootClassName}>
        <div className="wizard-header">
          <h1>
            <i className="fas fa-utensils"></i> Nutrition Planner
          </h1>
          <p className="wizard-subtitle">Your active nutrition plan</p>
        </div>

        <div className="np-summary-card">
          <div className="np-summary-header">
            <div className="np-summary-title-row">
              <i className="fas fa-bullseye"></i>
              <span className="np-summary-name">{activeGoal.name}</span>
              <span className="np-goal-type-badge">{activeGoal.goalType}</span>
              {activeGoal.isClientPlan && (
                <span className="np-coach-badge">
                  <i className="fas fa-user-tie"></i> Coach Plan
                </span>
              )}
            </div>
            <div className="np-summary-actions">
              <button
                className="np-manage-btn"
                onClick={() => navigate(`/nutrition/planner?goalId=${activeGoal._id}`)}
              >
                <i className="fas fa-edit"></i> Manage Plan
              </button>
              {!activeGoal.isClientPlan && (
                <button
                  className="np-delete-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                  title="Delete nutrition plan"
                >
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
          </div>

          <div className="np-summary-targets">
            <div className="np-target">
              <span className="np-target-value">{activeGoal.dailyCalories}</span>
              <span className="np-target-label">Daily Calories</span>
            </div>
            <div className="np-target highlight">
              <span className="np-target-value">{activeGoal.dailyProtein}g</span>
              <span className="np-target-label">Daily Protein</span>
            </div>
            <div className="np-target">
              <span className="np-target-value">{activeGoal.dailyCarbs}g</span>
              <span className="np-target-label">Daily Carbs</span>
            </div>
            <div className="np-target">
              <span className="np-target-value">{activeGoal.dailyFat}g</span>
              <span className="np-target-label">Daily Fat</span>
            </div>
            <div className="np-target">
              <span className="np-target-value">{activeGoal.mealsPerDay}</span>
              <span className="np-target-label">Meals/Day</span>
            </div>
          </div>

          <div className="np-summary-details">
            <div className="np-detail-row">
              <span><i className="fas fa-running"></i> Activity: <strong>{activeGoal.activityLevel}</strong></span>
              <span><i className="fas fa-weight"></i> Current: <strong>{lbsToDisplayWeight(activeGoal.currentWeight, weightUnit).value} {weightUnit}</strong></span>
              {activeGoal.goalWeight && (
                <span><i className="fas fa-flag"></i> Goal: <strong>{lbsToDisplayWeight(activeGoal.goalWeight, weightUnit).value} {weightUnit}</strong></span>
              )}
            </div>
            <div className="np-detail-row">
              <span><i className="fas fa-utensils"></i> Per Meal: <strong>{activeGoal.perMealCalories} cal</strong>, <strong>{activeGoal.perMealProtein}g protein</strong></span>
            </div>
          </div>
        </div>

        <div className="np-summary-cta">
          <button
            className="np-create-new-btn"
            onClick={() => {
              setActiveGoal(null);
            }}
          >
            <i className="fas fa-plus"></i> Create New Plan
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="np-delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="np-delete-modal" onClick={(e) => e.stopPropagation()}>
              <h4><i className="fas fa-exclamation-triangle"></i> Delete Nutrition Plan?</h4>
              <p>This will permanently delete your "{activeGoal.name}" plan and all its settings. This cannot be undone.</p>
              <div className="np-delete-actions">
                <button
                  className="np-cancel-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="np-confirm-delete-btn"
                  onClick={handleDeletePlan}
                  disabled={deleting}
                >
                  {deleting ? (
                    <><i className="fas fa-spinner fa-spin"></i> Deleting...</>
                  ) : (
                    <><i className="fas fa-trash"></i> Delete Plan</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {!embedded && (
        <div className="wizard-header">
          {goalId && (
            <button
              className="np-exit-wizard-btn"
              onClick={handleExit}
            >
              <i className="fas fa-arrow-left"></i>{' '}
              {isCoachMode ? 'Back to Dashboard' : isBusinessMode ? 'Back to Dashboard' : 'Back to Nutrition'}
            </button>
          )}
          <h1>
            <i className="fas fa-utensils"></i> Nutrition Planner
          </h1>
          <p className="wizard-subtitle">
            Build a personalized nutrition plan and shop for ingredients
          </p>
          {currentGoalId && (
            <span className="auto-save-badge">
              <i className="fas fa-cloud-upload-alt"></i> Auto-saving
            </span>
          )}
        </div>
      )}
      {embedded && (
        <div className="wizard-header wizard-header--embedded">
          <div className="embedded-header-row">
            <span className="embedded-header-title">
              <i className="fas fa-utensils"></i> Nutrition Planner
            </span>
            {currentGoalId && (
              <span className="auto-save-badge">
                <i className="fas fa-cloud-upload-alt"></i> Auto-saving
              </span>
            )}
            <button className="np-exit-wizard-btn" onClick={handleExit}>
              <i className="fas fa-times"></i> Close
            </button>
          </div>
        </div>
      )}

      {renderStepIndicator()}

      <div className="wizard-content">
        {renderStepContent()}
      </div>

      <div className="wizard-navigation">
        <button 
          className="nav-btn back-btn"
          onClick={goBack}
          disabled={currentStep === 1}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
        
        <div className="step-counter">
          Step {currentStep} of {STEPS.length}
        </div>
        
        {currentStep < STEPS.length ? (
          <button 
            className="nav-btn next-btn"
            onClick={goNext}
            disabled={!canGoNext()}
          >
            Next <i className="fas fa-arrow-right"></i>
          </button>
        ) : (
          <button
            className="nav-btn next-btn done-btn"
            onClick={handleExit}
          >
            <i className="fas fa-check"></i> Done
          </button>
        )}
      </div>
    </div>
  );
};

export default NutritionPlannerWizard;
